import React, { useEffect, useState } from 'react';
import { Modal } from '../../../common';
import CountryTab from './CountryTab';
import LanguageTab from './LanguageTab';
import { HiOutlineGlobeAlt } from 'react-icons/hi';
import { MdLanguage } from 'react-icons/md';
import { useTranslation } from 'react-i18next';
import { COUNTRIES, LANGUAGES } from '../../../static/region-data';
import ReactCountryFlag from 'react-country-flag';

const RegionLanguageModal = ({
  visible,
  onClose,
  initialTab = 'country',
  selectedCountry = 'US',
  selectedLang = 'en',
  onApply,
}) => {
  const { t } = useTranslation();
  useEffect(() => {
    setPendingCountry(selectedCountry);
    setPendingLang(selectedLang);
  }, [selectedLang, selectedCountry]);
  const TABS = [
    {
      id: 'country',
      label: t('regionLanguage.tabs.country'),
      Icon: HiOutlineGlobeAlt,
    },
    {
      id: 'language',
      label: t('regionLanguage.tabs.language'),
      Icon: MdLanguage,
    },
  ];
  const [activeTab, setActiveTab] = useState(initialTab);
  const [pendingCountry, setPendingCountry] = useState(selectedCountry);
  const [pendingLang, setPendingLang] = useState(selectedLang);

  const country = COUNTRIES.find((c) => c.code === pendingCountry);
  const lang = LANGUAGES.find((l) => l.code === pendingLang);

  const handleApply = () => {
    onApply?.(country, lang);
    onClose();
  };

  const handleClose = () => {
    // reset pending to last confirmed values on dismiss
    setPendingCountry(selectedCountry);
    setPendingLang(selectedLang);
    onClose();
  };

  return (
    <Modal
      visible={visible}
      onClose={handleClose}
      heading={t('regionLanguage.title')}
      description={t('regionLanguage.description')}
      size='xl'
      varient='primary'
      showHeader
      closeOnBackPress
      customStyles='sm:max-w-2xl'
      childrenStyles='!px-0 !pb-0 !pt-0'
    >
      {/* ── Tabs ── */}
      <div className='flex border-b border-white/10'>
        {TABS.map(({ id, label, Icon }) => (
          <button
            key={id}
            onClick={() => setActiveTab(id)}
            className={`flex-1 flex items-center justify-center gap-2 py-3.5 text-sm font-semibold transition-all relative
              ${
                activeTab === id
                  ? 'text-white'
                  : 'text-gray-500 hover:text-gray-300'
              }`}
          >
            <Icon size={16} />
            {label}
            {activeTab === id && (
              <span className='absolute bottom-0 left-0 right-0 h-0.5 bg-red-600 rounded-t-full' />
            )}
          </button>
        ))}
      </div>

      {/* ── Panel ── */}
      <div className='px-5 pt-4 pb-3'>
        {activeTab === 'country' ? (
          <CountryTab
            pendingCountry={pendingCountry}
            setPendingCountry={setPendingCountry}
          />
        ) : (
          <LanguageTab
            pendingLang={pendingLang}
            setPendingLang={setPendingLang}
          />
        )}
      </div>

      {/* ── Footer ── */}
      <div className='flex items-center justify-between gap-3 px-5 py-4 border-t border-white/10 bg-white/5 overflow-x-auto'>
        {/* Live preview */}
        <div className='flex items-center gap-3 text-sm'>
          {country && (
            <div className='flex items-center gap-1.5 text-gray-400'>
              <ReactCountryFlag countryCode={country.code} svg />

              <span className='text-white font-medium'>{country.name}</span>
              <span className='text-gray-600'>·</span>
              <span className='text-gray-500'>{country.currency}</span>
            </div>
          )}
          {lang && (
            <div className='flex items-center gap-1.5'>
              <span className='w-px h-4 bg-white/10' />
              <span className='text-base'>{lang.flag}</span>
              <span className='text-gray-400'>{lang.name}</span>
            </div>
          )}
        </div>

        <div className='flex gap-2 '>
          <button
            onClick={handleClose}
            className='px-5 py-2.5 rounded-xl text-sm font-medium text-gray-400 hover:text-white border border-white/10 hover:border-white/25 transition-all'
          >
            {t('regionLanguage.actions.cancel')}
          </button>
          {/* Step-through: country → language → apply */}
          {activeTab === 'country' ? (
            <button
              onClick={() => setActiveTab('language')}
              className='px-5 py-2.5 rounded-xl text-sm font-bold text-white bg-red-600 hover:bg-red-500 active:scale-95 transition-all'
            >
              {t('regionLanguage.actions.nextLanguage')}
            </button>
          ) : (
            <button
              onClick={handleApply}
              className='px-6 py-2.5 rounded-xl text-sm font-bold text-white bg-red-600 hover:bg-red-500 active:scale-95 transition-all'
            >
              {t('regionLanguage.actions.apply')}
            </button>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default RegionLanguageModal;
